/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TreeSelect, Spin } from 'antd';
import { useSelector } from 'react-redux';

import { getIdMap } from '../helpers/helpers';
import { getPhaseCostcodeTreeData } from '../forms/formHelpers';
import useProjectOrGlobalCostcodes from './useProjectOrGlobalCostcodes';
import useProjectCostcodesFetch from './useProjectCostcodesFetch';

export default function CostcodesTreeSelector({
  projectId,
  projectIds,
  phaseId,
  phaseIds,
  divisionId,
  divisionIds,
  isClockIn = false,
  includeGlobal = true,
  multiple = false,
  filterCostcode,
  includeCategoryIds = false,
  disabledKeys,
  onCostcodesFetched,
  value,
  onChange,
  ...restProps
}) {
  const { costcodes: globalCostcodes } = useProjectOrGlobalCostcodes({
    projectId: null,
    divisionId,
    divisionIds,
    visible: includeGlobal,
    includeCategoryIds,
  });

  const phases = useSelector((state) => state.costcodes.phases) ?? [];

  const effectiveProjectIds = useMemo(() => {
    if (projectIds?.length) return projectIds;
    if (projectId) return [projectId];
    return [];
  }, [projectId, projectIds]);

  const {
    projectCostcodes,
    loading,
    fetchNewIds,
  } = useProjectCostcodesFetch({
    projectIds: effectiveProjectIds,
    includeCategoryIds,
    onFetched: onCostcodesFetched,
  });

  const filteredProjectCostcodes = useMemo(() => (
    filterCostcode ? projectCostcodes.filter(filterCostcode) : projectCostcodes
  ), [projectCostcodes, filterCostcode]);

  const costcodeMap = useMemo(() => getIdMap(filteredProjectCostcodes), [filteredProjectCostcodes]);

  const allCostcodes = useMemo(() => {
    const base = includeGlobal ? [...globalCostcodes, ...projectCostcodes] : projectCostcodes;
    if (!filterCostcode) return base;
    return base.filter(filterCostcode);
  }, [includeGlobal, globalCostcodes, projectCostcodes, filterCostcode]);

  // Map of code → costcode for project-specific codes; used by getPhaseCostcodeTreeData
  // to suppress global duplicates when a matching project-specific code exists.
  const costcodeCodeMap = useMemo(() => {
    if (!effectiveProjectIds.length) return {};
    return getIdMap(projectCostcodes, 'code');
  }, [effectiveProjectIds, projectCostcodes]);

  const treeData = useMemo(() => {
    if (!effectiveProjectIds.length && !includeGlobal) return [];

    let result;

    if (phaseIds?.length) {
      result = getPhaseCostcodeTreeData({
        projectId: effectiveProjectIds[0] ?? null,
        phaseIds,
        phases,
        costcodes: allCostcodes,
        costcodeMap,
        includeGlobal,
        costcodeCodeMap,
        isClockIn,
        disabledKeys,
      });
    } else if (effectiveProjectIds.length > 1) {
      const data = [];
      effectiveProjectIds.forEach((id) => {
        const projectData = getPhaseCostcodeTreeData({
          projectId: id,
          phases,
          costcodes: allCostcodes,
          costcodeMap,
          includeGlobal: false,
          costcodeCodeMap,
          isClockIn,
          disabledKeys,
        });
        data.push(...projectData);
      });
      result = data;
    } else {
      result = getPhaseCostcodeTreeData({
        projectId: effectiveProjectIds[0] ?? null,
        phaseId,
        phases,
        costcodes: allCostcodes,
        costcodeMap,
        includeGlobal,
        costcodeCodeMap,
        isClockIn,
        disabledKeys,
      });
    }

    return result;
  }, [
    effectiveProjectIds,
    phaseId,
    phaseIds,
    phases,
    allCostcodes,
    costcodeMap,
    includeGlobal,
    costcodeCodeMap,
    isClockIn,
    disabledKeys,
  ]);

  return (
    <TreeSelect
      value={value}
      onChange={onChange}
      treeData={treeData}
      placeholder="Select a Cost Code"
      allowClear
      showSearch
      treeDefaultExpandAll
      treeNodeFilterProp="title"
      multiple={multiple}
      notFoundContent={loading ? <Spin size="small" /> : undefined}
      onDropdownVisibleChange={(open) => {
        if (open) fetchNewIds();
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...restProps}
    />
  );
}

CostcodesTreeSelector.propTypes = {
  projectId: PropTypes.string,
  projectIds: PropTypes.arrayOf(PropTypes.string),
  phaseId: PropTypes.string,
  phaseIds: PropTypes.arrayOf(PropTypes.string),
  divisionId: PropTypes.string,
  divisionIds: PropTypes.arrayOf(PropTypes.string),
  isClockIn: PropTypes.bool,
  includeGlobal: PropTypes.bool,
  multiple: PropTypes.bool,
  filterCostcode: PropTypes.func,
  includeCategoryIds: PropTypes.bool,
  disabledKeys: PropTypes.instanceOf(Set),
  onCostcodesFetched: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onChange: PropTypes.func,
};
